<script setup>
/**
 * O LOGO ANIMADO da casa — "adv up!" se montando, com o balão de conversa.
 *
 * Dois filmes SEM FUNDO, um por tema, gerados por `scripts/logo-animado.sh`
 * a partir dos originais do Victor (16/09/2026):
 *   claro  → "logo animada.mp4"
 *   escuro → "Então_mas_aí_por_exemplo_eu.mp4"
 *
 * O estúdio (fundo cinza/grafite, sombra no chão, selo ✦ do gerador) foi
 * removido quadro a quadro; o vídeo tem transparência de verdade e pousa em
 * qualquer superfície. webm VP9 com alfa para Chrome/Edge/Firefox, mov HEVC
 * com alfa para o Safari. Quem pede menos movimento vê o último quadro.
 *
 * `?v=` na URL: o nome do arquivo é o mesmo do vídeo antigo, e o navegador
 * continuava mostrando o velho, com o retângulo cinza.
 */
import { ref } from 'vue';

const props = defineProps({
  tamanho: { type: String, default: '13rem' },
  /**
   * Repetir o filme. O filme começa VAZIO (o logo se monta no 1º segundo) e
   * dura 10 s: repetindo, o logo some e remonta a cada 10 s — medido quadro a
   * quadro em 24/09. Onde o logo fica parado na tela por muito tempo (a
   * leitura vazia do E-mail), `false` monta uma vez e para no último quadro.
   * O padrão continua repetindo: as telas que já usavam não mudam.
   */
  repetir: { type: Boolean, default: true },
});

// Terminou (só sem repetir): troca o filme pela imagem parada do último
// quadro. O quadro final do <video> não é garantido — medido em 24/09: o
// filme terminado (`ended`, 10,0 s, pronto) aparecia VAZIO na tela depois de
// um tempo, o navegador solta a imagem de vídeo parado.
const parou = ref(false);
const aoTerminar = () => {
  if (!props.repetir) parou.value = true;
};

const VERSAO = '3';
const TIPO_HEVC = 'video/mp4; codecs="hvc1"';

const FILMES = {
  claro: {
    webm: `/brand-assets/chat-empty-light.webm?v=${VERSAO}`,
    mov: `/brand-assets/chat-empty-light.mov?v=${VERSAO}`,
    parado: `/brand-assets/chat-empty-light.webp?v=${VERSAO}`,
  },
  escuro: {
    webm: `/brand-assets/chat-empty-dark.webm?v=${VERSAO}`,
    mov: `/brand-assets/chat-empty-dark.mov?v=${VERSAO}`,
    parado: `/brand-assets/chat-empty-dark.webp?v=${VERSAO}`,
  },
};
</script>

<template>
  <div
    class="logo-animado"
    :class="{ 'logo-animado--parou': parou }"
    :style="{ '--logo-tamanho': tamanho }"
    aria-hidden="true"
  >
    <template v-for="(filme, tema) in FILMES" :key="tema">
      <video
        class="logo-animado__filme"
        :class="`logo-animado__filme--${tema}`"
        :poster="filme.parado"
        autoplay
        :loop="repetir"
        muted
        playsinline
        preload="auto"
        disablepictureinpicture
        @ended="aoTerminar"
      >
        <!-- O Safari lê o HEVC com alfa e ignora o webm; os outros não tocam
             HEVC e passam para o webm. A ordem importa. -->
        <source :src="filme.mov" :type="TIPO_HEVC" />
        <source :src="filme.webm" type="video/webm" />
      </video>
      <img
        class="logo-animado__parado"
        :class="`logo-animado__parado--${tema}`"
        :src="filme.parado"
        alt=""
      />
    </template>
  </div>
</template>

<style scoped>
.logo-animado {
  position: relative;
  width: min(var(--logo-tamanho), 70vw);
  aspect-ratio: 6 / 5;
  animation: logoChega var(--mov-4) var(--mov-curva) backwards;
}

@keyframes logoChega {
  from {
    opacity: 0;
    scale: 0.96;
  }
}

.logo-animado__filme,
.logo-animado__parado {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  user-select: none;
}

.logo-animado__filme--escuro {
  display: none;
}

.logo-animado__parado {
  display: none;
}

.dark .logo-animado__filme--claro {
  display: none;
}

.dark .logo-animado__filme--escuro {
  display: block;
}

/* Terminou: a imagem parada no lugar do filme, no tema certo. */
.logo-animado--parou .logo-animado__filme {
  display: none;
}

.logo-animado--parou .logo-animado__parado--claro {
  display: block;
}

.dark .logo-animado--parou .logo-animado__parado--claro {
  display: none;
}

.dark .logo-animado--parou .logo-animado__parado--escuro {
  display: block;
}

@media (prefers-reduced-motion: reduce) {
  .logo-animado {
    animation: none;
  }

  .logo-animado__filme--claro,
  .dark .logo-animado__filme--escuro {
    display: none;
  }

  .logo-animado__parado--claro {
    display: block;
  }

  .dark .logo-animado__parado--claro {
    display: none;
  }

  .dark .logo-animado__parado--escuro {
    display: block;
  }
}
</style>
